import { z } from 'zod';

export const ANNOUNCEMENT_BRAND = 'wudi_star' as const;
export const ANNOUNCEMENT_PLATFORMS = ['android', 'ios', 'tv_android', 'tv_ios', 'macos', 'windows'] as const;
export const AnnouncementPlatformSchema = z.enum(ANNOUNCEMENT_PLATFORMS);
export type AnnouncementPlatform = z.infer<typeof AnnouncementPlatformSchema>;
export const PLATFORM_LABELS: Record<AnnouncementPlatform, string> = {
  android: 'Android', ios: 'iOS', tv_android: 'Android TV', tv_ios: 'tvOS', macos: 'macOS', windows: 'Windows',
};
export const codePoints = (value: string) => Array.from(value).length;
export const utf8Bytes = (value: string) => new TextEncoder().encode(value).length;
const wellFormed = (value: string) => !Array.from(value).some(character => character.length === 1 && character.charCodeAt(0) >= 0xd800 && character.charCodeAt(0) <= 0xdfff);
export const AnnouncementTitleSchema = z.string().transform(value => value.trim()).refine(wellFormed, '標題含有無效的 Unicode 字元').refine(value => codePoints(value) >= 1 && codePoints(value) <= 200, '標題須為 1–200 個字元').refine(value => !/[\r\n]/.test(value), '標題不可換行');
export const AnnouncementBodySchema = z.string().transform(value => value.replace(/\r\n?/g, '\n')).refine(wellFormed, '內文含有無效的 Unicode 字元').refine(value => value.trim().length > 0, '請輸入公告內文').refine(value => codePoints(value) <= 20000 && utf8Bytes(value) <= 60000, '內文最多 20,000 個字元，且不可超過 60,000 UTF-8 bytes');
export const AnnouncementDateSchema = z.string().datetime({ offset: true }).refine(value => {
  const parsed = new Date(value);
  const year = parsed.getUTCFullYear();
  const match = /^(\d{4})-(\d{2})-(\d{2})T/.exec(value);
  if (!Number.isFinite(parsed.getTime()) || year < 1000 || year > 9999 || !match) return false;
  const [, rawYear, month, day] = match.map(Number);
  return month >= 1 && month <= 12 && day >= 1 && day <= new Date(Date.UTC(rawYear, month, 0)).getUTCDate();
}, '請輸入含時區的有效日期（UTC 年份 1000–9999）');
export const AnnouncementPlatformsSchema = z.array(AnnouncementPlatformSchema).min(1, '請選擇適用平台').max(6).refine(value => new Set(value).size === value.length, '平台不可重複');
export const AnnouncementFormSchema = z.object({
  targetBrand: z.literal(ANNOUNCEMENT_BRAND),
  title: AnnouncementTitleSchema,
  body: AnnouncementBodySchema,
  targetPlatforms: AnnouncementPlatformsSchema,
  expiryMode: z.enum(['none', 'at']),
  expiresAt: AnnouncementDateSchema.nullable(),
}).superRefine((value, context) => {
  if (value.expiryMode === 'at' && value.expiresAt === null) context.addIssue({ code: 'custom', path: ['expiresAt'], message: '請選擇到期時間' });
});
export type AnnouncementFormValues = z.infer<typeof AnnouncementFormSchema>;

export function draftPayload(values: AnnouncementFormValues) {
  const parsed = AnnouncementFormSchema.parse(values);
  return { targetBrand: parsed.targetBrand, title: parsed.title, body: parsed.body, targetPlatforms: [...parsed.targetPlatforms].sort(), expiresAt: parsed.expiryMode === 'none' ? null : new Date(parsed.expiresAt!).toISOString() };
}
